import { useState } from "react";
import { Plus } from "lucide-react";

import { API_BASE } from "@/config/api";
import { fetchWithAuth } from "@/lib/api";
import { BOOK_TYPE_OPTIONS, shouldShowBookMode } from "@/lib/book-types";
import type { Book, BookType, TWRMode } from "@/schemas";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface CreatePortfolioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (bookId: string) => void;
  onPortfoliosChanged: () => Promise<void> | void;
}

export function CreatePortfolioDialog({
  open,
  onOpenChange,
  onCreated,
  onPortfoliosChanged,
}: CreatePortfolioDialogProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState("");
  const [newPortfolioType, setNewPortfolioType] = useState<BookType | "">("");
  const [newPortfolioMode, setNewPortfolioMode] = useState<TWRMode | "">("");

  const handleCreatePortfolio = async () => {
    const requiresCalculationMode =
      newPortfolioType && shouldShowBookMode(newPortfolioType);

    if (
      !newPortfolioName.trim() ||
      !newPortfolioType ||
      (requiresCalculationMode && !newPortfolioMode)
    ) {
      return;
    }

    try {
      setIsCreating(true);
      const response = await fetchWithAuth(`${API_BASE}/books`, {
        method: "POST",
        body: JSON.stringify({
          name: newPortfolioName.trim(),
          provider: "manual",
          book_type: newPortfolioType,
          twr_mode: requiresCalculationMode ? newPortfolioMode : "simple",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to create portfolio");
      }

      const data = await response.json();
      const createdBook = (data.book ?? data) as Book;

      await onPortfoliosChanged();

      if (createdBook?.book_id) {
        onCreated(createdBook.book_id);
      }

      setNewPortfolioName("");
      setNewPortfolioType("");
      setNewPortfolioMode("");
      onOpenChange(false);
    } catch (error) {
      console.error("Error creating portfolio:", error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Create Portfolio</DialogTitle>
          <DialogDescription>
            Create a new portfolio container and choose its type.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="new-portfolio-name">Name</Label>
            <Input
              id="new-portfolio-name"
              placeholder="e.g. Retirement, Crypto, Long-Term"
              value={newPortfolioName}
              onChange={(event) => setNewPortfolioName(event.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-portfolio-type">Portfolio Type</Label>
            <Select
              value={newPortfolioType}
              onValueChange={(value) => setNewPortfolioType(value as BookType)}
            >
              <SelectTrigger id="new-portfolio-type">
                <SelectValue placeholder="Select a portfolio type" />
              </SelectTrigger>
              <SelectContent>
                {BOOK_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {
                BOOK_TYPE_OPTIONS.find(
                  (option) => option.value === newPortfolioType,
                )?.description
              }
            </p>
          </div>

          {newPortfolioType && shouldShowBookMode(newPortfolioType) ? (
            <div className="space-y-2">
              <Label htmlFor="new-portfolio-mode">Calculation Mode</Label>
              <Select
                value={newPortfolioMode}
                onValueChange={(value) => setNewPortfolioMode(value as TWRMode)}
              >
                <SelectTrigger id="new-portfolio-mode">
                  <SelectValue placeholder="Choose a calculation mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="advanced">
                    Advanced - includes cash flows in performance calculations
                  </SelectItem>
                  <SelectItem value="simple">
                    Simple - focuses on buy and sell activity only
                  </SelectItem>
                </SelectContent>
              </Select>
              {newPortfolioMode === "advanced" ? (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                  Advanced mode includes cash transactions in performance
                  calculations. If you are not sure, prefer Simple mode.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreatePortfolio}
            disabled={
              !newPortfolioName.trim() ||
              !newPortfolioType ||
              (shouldShowBookMode(newPortfolioType) && !newPortfolioMode) ||
              isCreating
            }
          >
            <Plus className="mr-2 h-4 w-4" />
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
